import React from "react";
import "@/App.css";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppProviders, useAuth } from "@/lib/providers";
import NavBar from "@/components/NavBar";
import Landing from "@/pages/Landing";
import Studio from "@/pages/Studio";
import NewProject from "@/pages/NewProject";
import ProjectDetail from "@/pages/ProjectDetail";
import AssessmentView from "@/pages/AssessmentView";
import Admin from "@/pages/Admin";
import Settings from "@/pages/Settings";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Support from "@/pages/Support";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refund from "@/pages/Refund";
import FAQ from "@/pages/FAQ";
import Framework from "@/pages/Framework";
import AssessmentDiff from "@/pages/AssessmentDiff";
import AuthCallback from "@/pages/AuthCallback";
import PendingApproval from "@/pages/PendingApproval";
import VariationSetView from "@/pages/VariationSetView";
import SiteFooter from "@/components/SiteFooter";
import CookieConsent from "@/components/CookieConsent";
import BackButton from "@/components/BackButton";
import Seo from "@/components/Seo";
import { Toaster } from "sonner";
import { useTheme } from "@/lib/providers";
import { getConsent, loadGoogleAnalytics, trackPageView } from "@/lib/analytics";

function Protected({ children, adminOnly }) {
  const { user, authLoading } = useAuth();
  if (authLoading) return <div className="v-container" style={{ paddingTop: 80 }}><div className="overline">Loading…</div></div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.status && user.status !== "active") return <PendingApproval />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/studio" replace />;
  return children;
}

function BrandedToaster() {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme === "dark" ? "dark" : "light"}
      position="top-right"
      richColors={false}
      closeButton
      toastOptions={{
        style: {
          fontFamily: "'Exo', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          background: "var(--v-surface)",
          color: "var(--v-text)",
          border: "1px solid var(--v-border)",
          fontSize: 14,
          letterSpacing: "-.005em",
          borderRadius: 2,
        },
        classNames: {
          success: "v-toast-success",
          error: "v-toast-error",
          info: "v-toast-info",
        },
      }}
    />
  );
}

function AppRouter() {
  const location = useLocation();

  // On first load, if the user already accepted analytics, boot GA up.
  React.useEffect(() => {
    if (getConsent() === "accepted") loadGoogleAnalytics();
  }, []);

  // Fire a SPA pageview every time the route changes — gtag will only see
  // these if loadGoogleAnalytics() has run (i.e. consent was accepted).
  React.useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Private product surfaces should never be indexed — wallet balances,
  // in-flight audits, admin dashboards, individual project pages. The
  // public pages (Landing, About, Pricing, Support, Terms, Privacy, Refund)
  // each render their own <Seo /> already.
  const PRIVATE_PREFIXES = [
    "/studio", "/admin", "/projects", "/assessments", "/variation-sets",
    "/settings", "/auth",
  ];
  const isPrivateRoute = PRIVATE_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  return (
    <>
      {isPrivateRoute && <Seo noIndex path={location.pathname} title="Verdict Studio" />}
      <NavBar />
      <BackButton />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<AuthCallback />} />
        <Route path="/studio" element={<Protected><Studio /></Protected>} />
        <Route path="/projects/new" element={<Protected><NewProject /></Protected>} />
        <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
        <Route path="/assessments/:id" element={<Protected><AssessmentView /></Protected>} />
        <Route path="/assessments/:id/diff" element={<Protected><AssessmentDiff /></Protected>} />
        <Route path="/variation-sets/:id" element={<Protected><VariationSetView /></Protected>} />
        <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
        <Route path="/settings" element={<Protected><Settings /></Protected>} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/support" element={<Support />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/refund" element={<Refund />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/framework" element={<Framework />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <SiteFooter />
      <CookieConsent />
      <BrandedToaster />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AppProviders>
        <BrowserRouter>
          <div className="App">
            <AppRouter />
          </div>
        </BrowserRouter>
      </AppProviders>
    </HelmetProvider>
  );
}
